import type { CSSProperties } from 'react'
import { useRef, useEffect } from 'react'
import { useCrosswordSettings } from '../../contexts/CrosswordSettingsContext'

type GridLetterProps = {
  letter: string
  x: number      // pixel position
  y: number      // pixel position
  size: number   // cellSize
  isConflict?: boolean | undefined
  className?: string | undefined
  isSelected?: boolean | undefined
  isHighlighted?: boolean | undefined
  isPreview?: boolean | undefined
  isEditable?: boolean | undefined // Can be edited (placeholder or temp word)
  isFocused?: boolean | undefined // Should have focus for typing
  isTemp?: boolean | undefined // Word is temporary (not saved to word list) - shows amber tint
  onInput?: ((value: string) => void) | undefined
}

/**
 * Single letter cell for the grid - absolutely positioned with its own border
 */
export function GridLetter({
  letter,
  x,
  y,
  size,
  isConflict = false,
  isSelected = false,
  isHighlighted = false,
  isPreview = false,
  isEditable = false,
  isFocused = false,
  isTemp = false,
  onInput,
  className,
}: GridLetterProps) {
  const { colors } = useCrosswordSettings()
  const ref = useRef<HTMLSpanElement>(null)

  // Calculate font size proportional to cell size
  const fontSize = Math.round(size * 0.55)

  // Focus the element when isFocused changes to true
  useEffect(() => {
    if (isFocused && isEditable && ref.current) {
      ref.current.focus()
      // Place cursor at end
      const range = document.createRange()
      const sel = window.getSelection()
      range.selectNodeContents(ref.current)
      range.collapse(false)
      sel?.removeAllRanges()
      sel?.addRange(range)
    }
  }, [isFocused, isEditable])

  const getColor = () => {
    // Conflicts take priority even in preview mode
    if (isConflict) return 'rgb(220 38 38)'
    if (isPreview) return 'rgb(59 130 246)' // blue-500
    return colors.normalText
  }

  const getBackgroundColor = () => {
    // Conflicts take priority even in preview mode
    if (isConflict) return colors.conflictBg // red-100 for better visibility
    if (isPreview) return 'rgb(219 234 254)' // blue-100
    if (isHighlighted) return 'rgb(220 252 231)' // green-100, softer
    // Temp words have no special background (yellow border instead)
    return undefined
  }

  const style: CSSProperties = {
    position: 'absolute',
    left: x,
    top: y,
    width: size,
    height: size,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize,
    boxSizing: 'border-box',
    color: getColor(),
    zIndex: isSelected ? 2 : isConflict ? 4 : undefined,
    backgroundColor: getBackgroundColor(),
    caretColor: 'rgb(59 130 246)', // blue-500 for cursor
    // outline: 'none',
  }

  const getOutlineClass = () => {
    // Conflicts take priority even in preview mode
    if (isConflict) return 'outline-red-500'
    if (isPreview) return 'outline-blue-400'
    if (isSelected) return 'outline-blue-500'
    if (isHighlighted) return 'outline-green-500'
    if (isTemp) return 'outline-amber-400'
    return 'outline-gray-800'
  }

  // Hide placeholder character (□) - show empty cell instead
  const displayLetter = letter === '□' ? '' : letter.toUpperCase()
  // Use zero-width space for empty cells so caret is visible
  const displayContent = displayLetter || (isEditable ? '\u200B' : '')

  const handleInput = (e: React.FormEvent<HTMLSpanElement>) => {
    const text = e.currentTarget.textContent?.replace(/\u200B/g, '') ?? ''
    // Only take the last character typed (in case of paste)
    const char = text.slice(-1).toUpperCase()
    if (/^[A-Z]$/.test(char)) {
      onInput?.(char)
    }
    // Reset content to prevent accumulation
    e.currentTarget.textContent = displayContent
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault()
      onInput?.('□')
    }
  }

  return (
    <span
      ref={ref}
      style={style}
      className={`outline font-medium font-sans ${getOutlineClass()} ${className}`}
      contentEditable={isEditable}
      suppressContentEditableWarning
      onInput={handleInput}
      onKeyDown={isEditable ? handleKeyDown : undefined}
    >
      {displayContent}
    </span>
  )
}
